import { User, UserManager } from 'oidc-client-ts';
import type { Environment } from './types';

// Wherever the page is served from. Each such origin must be a callback URL of the Cognito app client.
const redirectUrl = () => `${window.location.origin}/`;

let manager: UserManager;
let environment: Environment;
let currentUser: User | null = null;

function redirectToSignIn(): Promise<never> {
    manager.signinRedirect();
    // The page is navigating away.
    return new Promise(() => { });
}

// Resolves once there is a signed-in user, redirecting through the Cognito login when needed.
export async function signIn(env: Environment): Promise<User> {
    environment = env;
    manager = new UserManager({
        authority: env.COGNITO_AUTHORITY,
        client_id: env.COGNITO_CLIENT_ID,
        redirect_uri: redirectUrl(),
        response_type: 'code',
        scope: 'phone openid email',
        automaticSilentRenew: true,
    });
    manager.events.addUserLoaded((user) => { currentUser = user; });

    const params = new URLSearchParams(window.location.search);
    if (params.has('code') && params.has('state')) {
        try {
            currentUser = await manager.signinCallback() || null;
        } catch {
            // A stale or foreign callback (reload, or the round trip after signing out): start over.
            currentUser = null;
        }
        window.history.replaceState({}, document.title, window.location.pathname);
    } else {
        currentUser = await manager.getUser();
    }
    if (currentUser?.expired) {
        currentUser = await renew();
    }
    return currentUser || redirectToSignIn();
}

async function renew(): Promise<User | null> {
    try {
        return await manager.signinSilent();
    } catch {
        return null;
    }
}

export const idToken = () => currentUser?.id_token || '';
export const userEmail = () => String(currentUser?.profile.email || '');

// Called when the backend rejects the token: get a fresh one, or go through the login again.
export async function refreshSession(): Promise<void> {
    currentUser = await renew();
    if (!currentUser) {
        await redirectToSignIn();
    }
}

export async function signOut() {
    await manager.removeUser();
    const query = new URLSearchParams({
        client_id: environment.COGNITO_CLIENT_ID,
        response_type: 'code',
        redirect_uri: redirectUrl(),
    });
    // Ends the Cognito session and lands on its login page.
    window.location.href = `${environment.COGNITO_DOMAIN}/logout?${query}`;
}
